import useSWRImmutable from "swr/immutable";
import { useCosmosClient } from "@/services/cosmos";
import { JerkCardsResponseSchema } from "@/services/cosmos/schema/jerk";
import type { SavedCardsArgs } from "../types";

/**
 * SWR immutable hook for fetching saved cards from the Jerk service.
 *
 * Behavioral contract (must not change):
 * - SWR key: ["saved-cards", merchantId, orderId] (immutable — fetches once per merchant/order)
 * - Skipped when `skip` is true
 * - Falls back to empty cards + applePayPreference: false on null response
 * - Skips until merchantId is present so we never cache a response from an
 *   empty-merchant request (single-page checkout can mount payment UI before
 *   the session is fully hydrated).
 */
export function useSavedCards({
  merchantId,
  orderId,
  skip = false,
}: SavedCardsArgs) {
  const cosmosClient = useCosmosClient();
  const shouldSkip = skip || !merchantId;

  return useSWRImmutable(
    shouldSkip ? null : ["saved-cards", merchantId, orderId ?? ""],
    async () =>
      JerkCardsResponseSchema.parse(
        await cosmosClient.getSavedCards({
          merchantId,
          orderId: orderId ?? undefined,
        }),
      ) ?? {
        cards: [],
        metaData: { applePayPreference: false },
      },
  );
}
